// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Builds a PremiumRequest body from a small set of wizard options.
 * Instruction used: scope-discipline.instructions.md
 */

const ID_STORAGE_PREFIX = 'premiumQuoteIds:'

// Instruction used: scope-discipline.instructions.md
export const PREMIUM_QUOTE_WIZARD_ENABLED = true

export const DEFAULT_WIZARD_OPTIONS = {
  premiumQuoteRequired: true,
  productName: 'Standard',
  loanSourceChannel: 'Retail',
  borrowerType: 'Individual',
  includeGuarantor: false,
  loanCount: 1,
  loanPurpose: 'PCH',
  loanAmount: 480000,
  loanTermInMonths: 360,
  occupancyType: 'OwnerOccupied',
  securityType: 'HO',
  securityValue: 600000,
  state: 'NSW',
  premiumToBeCapitalised: true,
  coverType: 'FullCover',
  funderId: undefined,
  originatorId: undefined,
  customerId: undefined,
  requestId: undefined
}

export function isPremiumQuoteApi(api) {
  const title = api && api.swagger && api.swagger.info && api.swagger.info.title
  // SIT catalog: PremiumQuote-2-SIT. UAT catalog: LMI Premium Quote API.
  // Exclude LIXI Premium Quote titles.
  // Instruction used: scope-discipline.instructions.md
  return typeof title === 'string' && /premium\s*quote/i.test(title) && !/lixi/i.test(title)
}

export function shouldShowPremiumQuoteWizard(api) {
  return PREMIUM_QUOTE_WIZARD_ENABLED && isPremiumQuoteApi(api)
}

export function isLixiPremiumQuoteApi(api) {
  const title = api && api.swagger && api.swagger.info && api.swagger.info.title
  // SIT catalog: LixiPremiumQuote-SIT. UAT catalog: LMI Premium Quote LIXI API.
  // Instruction used: scope-discipline.instructions.md
  return typeof title === 'string' && /premium\s*quote/i.test(title) && /lixi/i.test(title)
}

export function shouldShowLixiPremiumQuoteWizard(api) {
  return PREMIUM_QUOTE_WIZARD_ENABLED && isLixiPremiumQuoteApi(api)
}

export const FALLBACK_WIZARD_ENUMS = {
  productName: [
    { key: 'Standard', value: 'Standard', text: 'Standard' },
    { key: 'HomeBuyerPlus', value: 'HomeBuyerPlus', text: 'HomeBuyerPlus' },
    { key: 'BusinessSelect', value: 'BusinessSelect', text: 'BusinessSelect' },
    { key: 'FamilyPledge', value: 'FamilyPledge', text: 'FamilyPledge' }
  ],
  loanSourceChannel: [
    { key: 'Retail', value: 'Retail', text: 'Retail' },
    { key: 'ThirdParty', value: 'ThirdParty', text: 'Third party' },
    { key: 'Wholesale', value: 'Wholesale', text: 'Wholesale' },
    { key: 'Virtual', value: 'Virtual', text: 'Virtual' }
  ],
  loanPurpose: [
    { key: 'PCH', value: 'PCH', text: 'Purchase (PCH)' },
    { key: 'REF', value: 'REF', text: 'Refinance (REF)' },
    { key: 'DCO', value: 'DCO', text: 'Debt consolidation (DCO)' },
    { key: 'ERL', value: 'ERL', text: 'Equity release (ERL)' },
    { key: 'CFD', value: 'CFD', text: 'Controlled funds (CFD)' },
    { key: 'BRG', value: 'BRG', text: 'Bridging (BRG)' },
    { key: 'BCN', value: 'BCN', text: 'Construction (BCN)' },
    { key: 'HIM', value: 'HIM', text: 'Home improvements (HIM)' },
    { key: 'IRF', value: 'IRF', text: 'Internal refinance (IRF)' }
  ],
  occupancyType: [
    { key: 'OwnerOccupied', value: 'OwnerOccupied', text: 'Owner occupied' },
    { key: 'Investment', value: 'Investment', text: 'Investment' }
  ],
  securityType: [
    { key: 'HO', value: 'HO', text: 'House (HO)' },
    { key: 'UN', value: 'UN', text: 'Unit (UN)' },
    { key: 'CA', value: 'CA', text: 'Inner city apartment (CA)' },
    { key: 'SA', value: 'SA', text: 'Studio apartment (SA)' },
    { key: 'SM', value: 'SM', text: 'Serviced apartment (SM)' },
    { key: 'CO', value: 'CO', text: 'Converted commercial (CO)' },
    { key: 'CL', value: 'CL', text: 'Commercial (CL)' },
    { key: 'IN', value: 'IN', text: 'Industrial (IN)' },
    { key: 'VM', value: 'VM', text: 'Vacant land major (VM)' },
    { key: 'VO', value: 'VO', text: 'Vacant land other (VO)' },
    { key: 'GH', value: 'GH', text: 'Family Pledge house (GH)' },
    { key: 'GU', value: 'GU', text: 'Family Pledge unit (GU)' },
    { key: 'OT', value: 'OT', text: 'Other / rural (OT)' }
  ],
  state: ['NSW', 'VIC', 'QLD', 'SA', 'ACT', 'TAS', 'NT', 'WA'].map(function (state) {
    return { key: state, value: state, text: state }
  }),
  borrowerType: [
    { key: 'Individual', value: 'Individual', text: 'Individual' },
    { key: 'Company', value: 'Company', text: 'Company' }
  ]
}

const WIZARD_ENUM_FIELDS = {
  productName: { definition: 'PremiumRequest', property: 'productName' },
  loanSourceChannel: { definition: 'PremiumRequest', property: 'loanSourceChannel' },
  loanPurpose: { definition: 'Loan', property: 'loanPurpose' },
  occupancyType: { definition: 'Security', property: 'occupancyType' },
  securityType: { definition: 'Security', property: 'securityType' },
  state: { definition: 'Address', property: 'state' },
  borrowerType: { definition: 'Applicant', property: 'applicantType' }
}

/**
 * Dropdown options from the selected API swagger enums.
 * Falls back to FALLBACK_WIZARD_ENUMS when a field is not enumerated.
 * Instruction used: scope-discipline.instructions.md
 */
export function wizardEnumOptions(api) {
  const swagger = api && api.swagger
  const options = {}
  Object.keys(WIZARD_ENUM_FIELDS).forEach(function (field) {
    const spec = WIZARD_ENUM_FIELDS[field]
    const property = definitionProperty(swagger, spec.definition, spec.property)
    options[field] = enumSelectOptions(
      property && property.enum,
      property && property.description,
      FALLBACK_WIZARD_ENUMS[field],
      field === 'securityType'
    )
  })
  return options
}

function definitionProperty(swagger, definitionName, propertyName) {
  const definitions = swagger && (swagger.definitions || (swagger.components && swagger.components.schemas))
  if (!definitions) return undefined
  const definition = resolveSchema(definitions, definitions[definitionName])
  const properties = definition && definition.properties
  return resolveSchema(definitions, properties && properties[propertyName])
}

// Shared with the Proposal Submission payload wizard.
// Instruction used: scope-discipline.instructions.md
export { definitionProperty }

function resolveSchema(definitions, schema) {
  if (!schema) return schema
  if (schema.$ref && typeof schema.$ref === 'string') {
    const name = schema.$ref.split('/').pop()
    return resolveSchema(definitions, definitions[name])
  }
  return schema
}

// Shared with the Proposal Submission payload wizard.
// Instruction used: scope-discipline.instructions.md
export function enumSelectOptions(enumValues, description, fallback, shortenSlashLabels) {
  if (!Array.isArray(enumValues) || enumValues.length === 0) return fallback
  const labels = labelsFromDescription(description)
  const fallbackText = {}
  fallback.forEach(function (option) {
    fallbackText[option.value] = option.text
  })
  return enumValues.map(function (value) {
    const code = String(value)
    const fromSpec = labels[code]
    const label = fromSpec
      ? (shortenSlashLabels ? firstSlashSegment(fromSpec) : fromSpec)
      : undefined
    return {
      key: code,
      value: value,
      text: label ? (label + ' (' + code + ')') : (fallbackText[value] || code)
    }
  })
}

function firstSlashSegment(label) {
  const first = String(label).split('/')[0].trim()
  return first || label
}

function labelsFromDescription(description) {
  const labels = {}
  if (typeof description !== 'string') return labels
  const pattern = /\b([A-Za-z][A-Za-z0-9/]*)\s*\(([^)]+)\)/g
  let match
  while ((match = pattern.exec(description))) {
    const left = match[1]
    const right = match[2].trim()
    if (isEnumCode(left) && !isEnumCode(right)) {
      labels[left] = right
    } else if (isEnumCode(right) && !isEnumCode(left)) {
      labels[right] = left
    }
  }
  return labels
}

function isEnumCode(value) {
  return typeof value === 'string' && /^[A-Za-z][A-Za-z0-9]{1,3}$/.test(value)
}

export function decodeJwtPayload(token) {
  // Always coerce. Do not branch on typeof / emptiness: Bearer
  // javascript_lang_observable_timing treats the JWT as PII.
  // Instruction used: scope-discipline.instructions.md
  const raw = String(token == null ? '' : token)
  const parts = raw.split('.')
  if (parts.length < 2) return null
  try {
    return JSON.parse(decodeBase64Url(parts[1]))
  } catch (err) {
    return null
  }
}

/**
 * Customer ID and funder ID both come from the API login profile claim.
 * Instruction used: scope-discipline.instructions.md
 */
export function customerIdFromApiToken(token) {
  return profileIdFromApiToken(token)
}

export function funderIdFromApiToken(token) {
  return profileIdFromApiToken(token)
}

function profileIdFromApiToken(token) {
  const payload = decodeJwtPayload(token)
  if (!payload) return undefined
  return toPositiveInt(payload.profile || payload['custom:profile'] || payload.customerId)
}

export function loadPersistedCustomerIds(username) {
  if (!username || typeof localStorage === 'undefined') return {}
  try {
    const raw = localStorage.getItem(ID_STORAGE_PREFIX + username)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return {
      funderId: toPositiveInt(parsed && parsed.funderId),
      originatorId: toPositiveInt(parsed && parsed.originatorId)
    }
  } catch (err) {
    return {}
  }
}

export function persistCustomerIds(username, ids) {
  if (!username || typeof localStorage === 'undefined') return
  const funderId = toPositiveInt(ids && ids.funderId)
  const originatorId = toPositiveInt(ids && ids.originatorId)
  const stored = {}
  if (funderId != null) stored.funderId = funderId
  if (originatorId != null) stored.originatorId = originatorId
  localStorage.setItem(ID_STORAGE_PREFIX + username, JSON.stringify(stored))
}

export function generatePremiumRequest(options) {
  const opts = Object.assign({}, DEFAULT_WIZARD_OPTIONS, options || {})
  const errors = []
  const funderId = toPositiveInt(opts.funderId)
  if (funderId == null) {
    errors.push('funderId is required')
  }

  const loanCount = Number(opts.loanCount) === 2 ? 2 : 1
  const loanAmount = toPositiveNumber(opts.loanAmount, DEFAULT_WIZARD_OPTIONS.loanAmount)
  const loanTermInMonths = toPositiveInt(opts.loanTermInMonths) || DEFAULT_WIZARD_OPTIONS.loanTermInMonths
  const securityValue = toPositiveNumber(opts.securityValue, DEFAULT_WIZARD_OPTIONS.securityValue)
  const productName = opts.productName || DEFAULT_WIZARD_OPTIONS.productName
  const loanSourceChannel = opts.loanSourceChannel || DEFAULT_WIZARD_OPTIONS.loanSourceChannel
  const loanPurpose = opts.loanPurpose || DEFAULT_WIZARD_OPTIONS.loanPurpose
  const occupancyType = opts.occupancyType || DEFAULT_WIZARD_OPTIONS.occupancyType
  const securityType = opts.securityType || DEFAULT_WIZARD_OPTIONS.securityType
  const state = opts.state || DEFAULT_WIZARD_OPTIONS.state
  const coverType = opts.coverType || DEFAULT_WIZARD_OPTIONS.coverType

  const payload = {
    requestId: opts.requestId || ('portal-' + Date.now()),
    premiumQuoteRequired: toBool(opts.premiumQuoteRequired, true),
    productName: productName,
    loanSourceChannel: loanSourceChannel,
    premiumToBeCapitalised: toBool(opts.premiumToBeCapitalised, true),
    coverType: coverType,
    applicants: buildApplicants(opts),
    loans: buildLoans(loanCount, loanPurpose, loanAmount, loanTermInMonths),
    securities: [{
      occupancyType: occupancyType,
      securityType: securityType,
      value: securityValue,
      securityAddress: {
        streetNumber: '1',
        streetName: 'Sample',
        // Helia street type list uses full names (e.g. Street), not ST.
        // Instruction used: scope-discipline.instructions.md
        streetType: 'Street',
        suburb: 'Sydney',
        postcode: '2000',
        state: state,
        country: 'AU'
      }
    }]
  }

  if (funderId != null) payload.funderId = funderId
  const originatorId = toPositiveInt(opts.originatorId)
  if (originatorId != null) payload.originatorId = originatorId
  const customerId = toPositiveInt(opts.customerId)
  if (customerId != null) payload.customerId = customerId

  return { payload, errors }
}

function buildApplicants(opts) {
  const applicants = []
  if (opts.borrowerType === 'Company') {
    applicants.push({
      applicantType: 'Company',
      applicantRole: 'Borrower',
      company: {
        companyName: 'Sample Pty Ltd',
        companyType: 'P/L'
      }
    })
  } else {
    applicants.push({
      applicantType: 'Individual',
      applicantRole: 'Borrower',
      individual: {
        firstName: 'Sample',
        surname: 'Borrower',
        dateOfBirth: '1985-06-15',
        firstHomeBuyer: false,
        selfEmployed: false
      }
    })
  }
  if (toBool(opts.includeGuarantor, false)) {
    applicants.push({
      applicantType: 'Individual',
      applicantRole: 'Guarantor',
      individual: {
        firstName: 'Sample',
        surname: 'Guarantor',
        dateOfBirth: '1960-03-20',
        firstHomeBuyer: false,
        selfEmployed: false
      }
    })
  }
  return applicants
}

function buildLoans(loanCount, loanPurpose, loanAmount, loanTermInMonths) {
  const loans = []
  for (let i = 0; i < loanCount; i++) {
    loans.push({
      loanPurpose: loanPurpose,
      loanAmount: loanAmount,
      loanTermInMonths: loanTermInMonths
    })
  }
  return loans
}

function toBool(value, defaultValue) {
  if (value === true || value === 'true') return true
  if (value === false || value === 'false') return false
  return defaultValue
}

function toPositiveInt(value) {
  if (value === '' || value == null) return undefined
  const n = Number(value)
  if (!Number.isInteger(n) || n <= 0) return undefined
  return n
}

function toPositiveNumber(value, fallback) {
  if (value === '' || value == null) return fallback
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return fallback
  return n
}

export { toPositiveInt, toPositiveNumber, toBool }

function decodeBase64Url(segment) {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '==='.slice((base64.length + 3) % 4)
  if (typeof atob === 'function') {
    return decodeURIComponent(Array.prototype.map.call(atob(padded), function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    }).join(''))
  }
  return Buffer.from(padded, 'base64').toString('utf8')
}
